import React from 'react';
import type { GetCatalogProductDetailsQuery } from '../../../../api/queries/getCatalogProductDetails/getCatalogProductDetails.graphql.generated';
import { ProductCatalogStorefrontStatus, ProductCatalogItemTilePresentationType, ProductUniversalItemSource } from '@graphql/generated-base';
import type { CatalogItemTile, ExternalProductOffer } from '../../../../Catalog.types';
import { SkeletonThumbnail, SkeletonText } from '@shared/components/Skeleton';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { ProductTile } from '../../../ProductTile';
import { useCatalogContext } from '../../../../state/Catalog';

export type OnProductOfferClickArgs = {
  itemPosition: number;
  offer: ExternalProductOffer;
};

export type OnQuickAddClickArgs = OnProductOfferClickArgs & {
  quantity: number;
};

export type ExternalProductOfferTileProps = {
  index: number;
  offer: ExternalProductOffer;
  isMobile?: boolean;
  keyPrefix?: string;
  onProductClick?: (args: OnProductOfferClickArgs) => void;
  onQuickAddClick?: (args: OnQuickAddClickArgs) => void;
};

const externalProductToCatalogItem = (offer: ExternalProductOffer): GetCatalogProductDetailsQuery['productCatalogItemById'] & CatalogItemTile => {
  const photos = [offer.productThumbnail].map(thumbnail => ({ url: thumbnail.url, id: thumbnail.url }));
  const listPriceRange = {
    isSinglePrice: true,
    lowerPrice: offer.sellerFullPrice,
    upperPrice: offer.sellerFullPrice,
  };
  const salePriceRange = offer.sellerSalePrice
    ? {
        isSinglePrice: true,
        lowerPrice: offer.sellerSalePrice,
        upperPrice: offer.sellerSalePrice,
      }
    : undefined;

  // don't show a Rating for external products, to make them less compelling than Catalog
  const rating = undefined;

  // don't show a Sales Price for external products
  //   sales are temporary; if the Couple doesn't add the item at full price, we lose margin
  const isFullPrice = true;
  const fullPrice = offer.sellerFullPrice;

  return {
    id: offer.productId,
    title: offer.productTitle,
    itemType: 'gift',
    tags: [],
    mediaAssets: [],
    // external offers aren't tracked in Joy's catalog inventory, so they're never considered Out of Stock
    inventory: {
      maxQty: Number.MAX_SAFE_INTEGER,
      __typename: 'ProductInventoryResult',
    },
    source: ProductUniversalItemSource.googleShoppingRegular,
    storefrontInfo: {
      canAddToCart: false,
      canAddToRegistry: true,
      storefrontStatus: ProductCatalogStorefrontStatus.available,
    },
    brand: {
      id: offer.sellerName,
      name: offer.sellerName,
      publicUrlPath: location.origin,
    },
    rating,
    price: fullPrice,
    fullPrice,
    isFullPrice,
    parentCategories: [],
    subCategories: [],
    photos,
    collectionDisplayInfos: [],
    groupDimensionOptions: [],
    tilePresentation: {
      photos,
      presentationType: ProductCatalogItemTilePresentationType.parentDetails,
      title: offer.productTitle,
      fullPriceRange: listPriceRange,
      priceRange: salePriceRange ?? listPriceRange,
      isFullPrice,
    },
  };
};

export const ExternalProductTile = ({
  index,
  keyPrefix,
  isMobile,
  onQuickAddClick,
  onProductClick,
  offer,
  addItemLoading,
}: ExternalProductOfferTileProps & { addItemLoading: boolean }) => {
  const item = externalProductToCatalogItem(offer);
  const { isProductInRegistry } = useCatalogContext();
  return (
    <ProductTile
      index={index}
      key={keyPrefix ?? '' + item.id}
      addItemLoading={addItemLoading}
      product={item}
      deriveTelemetryPageForProduct={() => 'searchResults'}
      addCatalogItem={args => {
        onQuickAddClick?.({ itemPosition: index, offer, quantity: args.payload.qty });
      }}
      onQuickAddClick={event => {
        event.stopPropagation();
        onQuickAddClick?.({ itemPosition: index, offer, quantity: 1 });
      }}
      onDetailClick={event => {
        event.stopPropagation();
        onProductClick?.({ itemPosition: index, offer });
      }}
      categoryType={'category'}
      isMobile={isMobile}
      hasAnyStorefrontWarning={false}
      componentSource={'productGrid'}
      variant={'gridItem'}
      shouldOpenPdp={false}
      skipGetPdpData={true}
      isItemAddedToRegistry={isProductInRegistry(item.id)}
    />
  );
};

const ProductTileMetadata: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={1}
      marginTop={4}
      {...rest}
    >
      {children}
    </Flex>
  );
};

ExternalProductTile.Skeleton = () => {
  return (
    <Box>
      <SkeletonThumbnail
        width={'100%'}
        height={'unset'}
        paddingTop="100%"
      />
      <ProductTileMetadata
        marginTop={5}
        rowGap={3}
      >
        <SkeletonText
          rows={2}
          spacing={3}
          skeletonHeight={[pxToRem(16), null, null, pxToRem(18)]}
        />
        <SkeletonText
          rows={1}
          skeletonHeight={pxToRem(14)}
          width={9}
        />
        <SkeletonText
          rows={1}
          skeletonHeight={pxToRem(14)}
          width={pxToRem(80)}
        />
      </ProductTileMetadata>
    </Box>
  );
};
